import alt from '../alt'
import FormActions from '../actions/FormActions'
import { formatAddress, formatLocationTitle } from '../utils/format'
import { validLocation } from '../utils/validate'
import moment from 'moment'
import sha1 from 'sha1'

const formatLocationWithId = location => {
  // NOTE: Rename locationId to id
  const { locationId: id, ...rest } = location
  return { id, ...rest }
}

const getExeptionZoneInfo = (info) => {
  const title = info && info.exceptionZone && info.exceptionZone.name ? info.exceptionZone.name  : '';
  const infoArr = info && info.locationInformation ? info.locationInformation.filter(item => ["EXCEPTION_ZONE_INFO"].includes(item.tag || '')) : [];
  const description = infoArr[0] ? infoArr[0].message : null;
  return title + (description ? ' – ' + description : '');
}

class FormStore {
  constructor () {
    this.bindActions(FormActions)
    this.data = this.emptyData()
    this.timer = null
    this.departures = []
    this.destinations = []
    this.accounts = []
    this.loading = false
    this.validating = false
    this.showAccountDetails = false
    this.showDepartureField = false
    this.showInfoMessage = false
    this.hasEstimate = false
    this.expiresAt = null
    this.estimateLoading = false
    this.estimateTimer = null
    this.estimateDeparture = null
    this.estimateDestination = null
    this.needFlightNumber = false
    this.disablePremium = false
    this.isAirport = false
    this.needBaggage = false
    this.allowDirectBook = true
    this.locationMessage = null
    this.directBookMessage = null
    this.user = null
    window.store = this
  }

  onUpdateTime () {
    this.data.preBooking = {
      edited: this.data.preBooking.edited,
      date: moment().add(1, 'minute').format('YYYY-MM-DD'),
      time: moment().add(1, 'minute').format('HH:mm')
    }
  }

  killTimer () {
    if (this.timer) {
      clearInterval(this.timer)
    }
  }

  startTimer () {
    this.killTimer()
    this.timer = setInterval(() => {
      FormActions.updateTime()
    }, 15000)
  }

  startEstimateTimer () {    
    if (this.data.fixedPrice && Date.now() < this.expiresAt) {
      console.log('startEstimateTimer')
      this.estimateTimer = setTimeout(() => {
        console.log('Estimate expired. Fetching a new estimate.')
        FormActions.estimatePrice(this.data)
      }, this.expiresAt - Date.now())
    }
  }

  onKillEstimateTimer () {
    console.log('onKillEstimateTimer')
    if (this.estimateTimer) {
      clearTimeout(this.estimateTimer)
      this.estimateTimer = null
    }
  }  

  onInitLocationParams (params) {
    const displayName = `${params.address.streetName || ''}${params.address.streetNumber ? ' ' : ''}${params.address.streetNumber || ''}${params.address.entrance || ''}, ${params.address.city || ''}`
    this.data.token = params.token
    this.data.location = {
      name: params.name,
      message: params.message,
      departure: {
        id: params.address.locationId
      },
      // NOTE: Passenger object is used throughout the app as state for selected account but the API does not include it
      // passenger: params.passenger,
      passenger: {},
      phone: params.phone,
      displayName: displayName
    }
    this.accounts = params.accounts ? params.accounts : []
    this.showDepartureField = params.showDepartureField ? params.showDepartureField : false

    this.startTimer()
  }

  onSelectCarType (name) {
    this.data.carTypes.forEach(carType => {
      carType.checked = carType.name === name
    })
  }

  onSelectAttribute (attributeName) {
    this.data.attributes.forEach(attribute => {
      if (attribute.name === attributeName) {
        attribute.checked = !attribute.checked
      }
    })
  }

  onSelectAttributeValue (attributeName) {
    this.data.attributes.forEach(attribute => {
      if (attribute.name === attributeName) {
        let val = attribute.numberSelect.value
        val = val + 1 > 2 ? 0 : val + 1
        attribute.numberSelect.value = val
        attribute.checked = val > 0
      }
    })
  }

  onSelectPriceType (priceType) {
    this.data.priceType = priceType
  }

  onInputChange ({ type, value }) {
    if (type === 'departure') {
      this.data.departure.selected = false
      this.data.departure.displayName = value

      this.data.departure.streetNumberToolTipVisible = false
      this.data.departure.redirectMessageVisable = false
      this.data.departure.redirectMessage = null

      // if (this.needFlightNumber) {
        this.needFlightNumber = false
        this.disablePremium = false
        this.isAirport = false
        this.needBaggage = false
        this.allowDirectBook = true
        this.locationMessage = null
        this.data.flightNumber = ''
        this.data.baggage = null
      // }    
    }
    else if (type === 'destination') {
      this.data.destination.selected = false
      this.data.destination.displayName = value

      this.data.destination.streetNumberToolTipVisible = false
      this.data.destination.redirectMessageVisable = false
      this.data.destination.redirectMessage = null
    }
    else if (type === 'phone') {
      this.data.phone = value
    }
    else {
      this.data[type] = value
    }
  }

  onDateChange ({ value }) {
    const dateVal = moment(value, 'YYYY-MM-DD').format('YYYY-MM-DD')
    if (!this.data.preBooking.edited && this.data.preBooking.date !== dateVal) {
      this.killTimer()
      this.data.preBooking.edited = true
    }
    this.data.preBooking.date = dateVal
  }

  onTimeChange ({ value }) {
    const wasDirectBooking = !this.data.preBooking.edited
    if (!this.data.preBooking.edited && this.data.preBooking.time !== value) {
      this.killTimer()
      this.data.preBooking.edited = true
    }
    this.data.preBooking.time = value
    
    // If we just switched from direct booking to pre-booking, recheck location info
    if (wasDirectBooking && this.data.preBooking.edited) {
      this.recheckLocationInformation()
    }
  }

  onAccountChange ({ value }) {
    if (isNaN(value)) {
      this.data.reference = ''
      delete this.data.account
      return delete this.data.location.passenger.payment
    }

    this.data.account = this.accounts.filter(account => account.number === value)[0]

    this.data.location.passenger.payment = Object.assign({}, this.data.location.passenger.payment, {
      account: value
    })
  }

  onSetBaggage (baggage) {
    this.data.baggage = baggage
  }

  onSetLoading ({ loading, bookingSuccess }) {
    this.loading = loading
    if (!loading && bookingSuccess) {
      this.reset(this.data.token, this.data.location)
    }
  }

  onSetValidating (validating) {
    this.validating = validating
  }

  onSetEstimateLoading (estimateLoading) {
    this.estimateLoading = estimateLoading
  }

  onSetFixedPrice (fixedPrice) {
    this.data.fixedPrice = fixedPrice
  }

  onSetSendSMS (sendSMS) {
    this.data.sendSMS = sendSMS
  }

  onSetShowInfoMessage (showInfoMessage) {
    this.showInfoMessage = showInfoMessage
  }

  onClearDeparture () {
    this.data.useLocationAsDestination = false
  }

  onClearDestination () {
    this.data.flightNumber = ''
    this.data.baggage = null;
    this.data.destination = {
      streetNumberToolTipVisible: false,
      redirectMessageVisable: false,
      redirectMessage: null,
      selected: false,
      displayName: ''
    }
    this.recheckLocationInformation();
  }

  onReceivedDestinations ({ type, destinations }) {
    if (type == 'departure') {
      this.departures = destinations
    } else {
      this.destinations = destinations
    }
  }

  onDestinationSelected ({ type, destination }) {
    console.log('onDestinationSelected:', type, destination)
    if (type == 'departure') {
      this.departures = []
    }
    else { 
      this.destinations = []
    }
    this.data[type].location = destination

    // v3, if incomplete location, show street number tool tip, set displayname to street address + space
    const isComplete = validLocation(destination)

    const displayName = isComplete ? formatLocationTitle(destination) : destination.address.streetName + ' '
    this.data[type].displayName = displayName
    this.data[type].selected = destination.locationInformation !== undefined && destination.bookable !== false
    this.data[type].streetNumberToolTipVisible = !isComplete

    // Check location information (if a redirect location we already have the info and do not need to do a location lookup)
    if (this.data[type].selected) this.checkLocationInformation(destination, type === 'departure')
    if(type === 'destination') this.recheckLocationInformation()
    // Hide redirect message?
    if (this.data[type].redirectMessageVisable) {
      this.data[type].redirectMessageVisable = !(destination.locationInformation !== undefined && destination.bookable !== false)
      if(!this.data[type].redirectMessageVisable) this.data[type].redirectMessage = null
    }
  }

  onReceivedLocationInfo ({ type, info }) {
    console.log('onReceivedLocationInfo:', type, info)
    this.data[type].location = formatLocationWithId(info)
    this.data[type].selected = info.bookable

    // v3 handle reddirect locations
    if (info.bookable === false && info.exceptionZone && 
      info.exceptionZone.redirectLocations && info.exceptionZone.redirectLocations.length > 0) {
      console.log('Got an exception zone')
      //this.data[type].redirectMessageVisable = true
      const listName = (type === 'departure') ? 'departures' : 'destinations'
      this[listName] = info.exceptionZone.redirectLocations.map(formatLocationWithId).filter(item => {
        // TODO: v3 Filter locations depending on TAG insted of type
        if (listName === 'departures') return item.locationInformation ? item.locationInformation.filter(info => ['NO_PICKUP'].includes(info.tag)).length === 0 : false
        if (listName === 'destinations') return item.locationInformation ? item.locationInformation.filter(info => ['NO_DROP_OFF'].includes(info.tag)).length === 0 : false
        return false
      })
      
      if(this[listName].length !== 0) {
        this.data[type].redirectMessageVisable = true
        this.data[type].redirectMessage = getExeptionZoneInfo(info)
      }
      return
    }

    this.checkLocationInformation(info, type === 'departure')
  }

  checkLocationInformation(info, isDeparture) {
    if (info.locationInformation) {
      info.locationInformation.forEach((row) => {
        if (row.tag === 'FLIGHT_NBR' && isDeparture && (this.data.preBooking.edited)) {
          this.needFlightNumber = true
          this.disablePremium = false
          this.isAirport = true
          this.needBaggage = true
          this.data.useLocationAsDestination = true
        }
        else if (row.tag === 'FLIGHT_NBR' && isDeparture && (!this.data.preBooking.edited)) {
          this.needFlightNumber = false
          // if(info.address.streetName === 'Arlanda') this.disablePremium = true
          this.isAirport = true
          this.needBaggage = false
          this.data.useLocationAsDestination = true
          // Override and disable direct book for Arlanda for now.
          if(info.address.streetName === 'Arlanda') {
            this.isAirport = true
            this.needFlightNumber = true
            this.needBaggage = true
            this.allowDirectBook = false
            this.directBookMessage = 'Direktbokning är inte tillåten för denna plats. Välj en senare hämtningstid.'
          }
        }
        else if (row.tag === 'NO_DIRECT_BOOK' && isDeparture) {
          this.allowDirectBook = false
          this.directBookMessage = row.message || 'Direktbokning är inte tillåten för denna plats. Välj en senare hämtningstid.'
        }
        else if (row.tag === 'DEPARTURE_INFO' && isDeparture) {
          this.locationMessage = row.message
        }
        else if (row.tag === 'INFO') {
          this.locationMessage = row.message
        }
      })
    }
  }

  recheckLocationInformation() {
    console.log('recheckLocationInformation');
    if (this.data.departure.location) {
      this.checkLocationInformation(this.data.departure.location, true)
    }
    /*if (this.data.destination.location) {
      this.checkLocationInformation(this.data.destination.location, false)
    }*/
  }

  onDisplayErrorMessage ({ message, code }) {
    this.data.error = {
      message,
      code
    }
  }

  onAccountPasswordChange (value) {
    this.data.location.passenger.payment = Object.assign({}, this.data.location.passenger.payment, {
      accountPassword: sha1(value.toUpperCase())
    })
  }

  onReferenceChange (value) {
    this.data.reference = value
  }

  onShowAccountDetails (value) {
    this.showAccountDetails = value
  }

  onReceivedPriceEstimate (estimateResponse) {
    console.log('onReceivedPriceEstimate:', estimateResponse)
    let numEstimates = 0

    estimateResponse.prices.forEach((estimate) => {      
      let name = 'taxi'
      if (estimate.serviceType === 'largeCar') name = 'storbil'
      else if (estimate.serviceType == 'business') name = 'business' 

      // Find cartype
      const carType = this.data.carTypes.find((item) => item.name == name)

      if (carType) {
        // If there's a price
        if (estimate.priceComponents && estimate.priceComponents.length > 0) {
          carType.estimate = estimate
          carType.estimate.priceToken = estimateResponse.priceToken
          numEstimates++
        }          

        // If there is no price
        else {
          carType.estimateNotAvailable = true
        }
      }
    })

    if (numEstimates > 0) {
      this.hasEstimate = true
      this.expiresAt = new Date(estimateResponse.validUntil).getTime() - (60 * 1000)
      this.estimateDeparture = JSON.stringify(estimateResponse.originLocation)
      this.estimateDestination = JSON.stringify(estimateResponse.destinationLocation)
      this.startEstimateTimer()
    }
  }

  onClearPriceEstimate() {
    this.data.carTypes.forEach((carType) => {
      delete carType.estimate
      delete carType.estimateNotAvailable
    })
    this.hasEstimate = false
    this.expiresAt = null
    this.estimateDeparture = null
    this.estimateDestination = null
    this.onKillEstimateTimer()
  }

  onReceivedUser(user) {
    this.user = user
  }

  emptyData (token, location) {
    return {
      name: '',
      departure: {
        streetNumberToolTipVisible: false,
        redirectMessageVisable: false,
        redirectMessage: null,
        selected: false,
        displayName: ''
      },
      destination: {
        streetNumberToolTipVisible: false,
        redirectMessageVisable: false,
        redirectMessage: null,
        selected: false,
        displayName: ''
      },
      carTypes: [
        {
          name: 'taxi',
          label: 'Taxi',
          labelSmall: '4 pers',
          checked: true
        },
        {
          name: 'business',
          label: 'Business',
          labelSmall: '3 pers',
          extraInfo: 'Tjänsten medför ett konstnadstillägg. Väljer du fastpris ser du skillnaden mellan vanlig taxi och business. 5 minuters väntetid ingår.',
          checked: false
        },
        {
          name: 'storbil',
          label: 'Storbil',
          labelSmall: '7 pers',
          extraInfo: 'Tjänsten medför ett kostnadstillägg.',
          checked: false
        }
      ],
      attributes: [
        {
          name: 'djur',
          label: 'Djur',
          checked: false
        },
        {
          name: 'bilkudde',
          label: 'Bilkudde',
          checked: false,
          numberSelect: {
            value: 0
          }
        },
        {
          name: 'kombi',
          label: 'Kombi',
          checked: false
        }
      ],
      preBooking: {
        edited: false,
        date: moment().add(1, 'minute').format('YYYY-MM-DD'),
        time: moment().add(1, 'minute').format('HH:mm')
      },
      phone: '',
      email: '',
      message: '',
      sendSMS: true,
      flightNumber: '',
      baggage: null,
      priceType: 'taxameter',
      location: location ? location : {
        passenger: {
          payment: {
            account: '',
            accountPassword: ''
          },
          phone: null
        }
      },
      token: token ? token : '',
      fixedPrice: false,
      reference: '',
      useLocationAsDestination: false,
      error: {}
    }
  }

  reset (token, location) {
    this.data = this.emptyData(token, location)
    delete this.data.location.passenger.payment
    this.timer = null
    this.destinations = []
    this.loading = false
    this.hasEstimate = false
    this.expiresAt = null
    this.estimateLoading = false    
    this.estimateDeparture = null
    this.estimateDestination = null
    this.needFlightNumber = false
    this.disablePremium = false;
    this.isAirport = false;
    this.needBaggage = false
    this.allowDirectBook = true
    this.locationMessage = null
    this.directBookMessage = null
    this.startTimer()
    this.onKillEstimateTimer()
  }
}

module.exports = alt.createStore(FormStore, 'FormStore')



// WEBPACK FOOTER //
// ./src/stores/FormStore.js